import { useAtom, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo } from 'react';

import { PointOfInterestChecked } from '@/types';
import * as localState from '../state';
import { SelectedPoiContent } from './SelectedPoiContent';
import { useFeatureFlag } from '@/hooks';
import { PointsOfInterestByType } from './PointsOfInterestByType';
import { PointsOfInterestByType as ReviewPointsOfInterestByType } from '@/components/Analysis/ReviewScreen/PointsOfInterestByType';
import * as analysisState from '@/components/Analysis/state';

type PointOfInterestTabProps = {
  pois: PointOfInterestChecked[];
  changeTab: (selectedTabIndex: number) => void;
  allPOILoading?: boolean;
};

export const PointOfInterestTab = ({ pois, changeTab, allPOILoading }: PointOfInterestTabProps) => {
  const [selectedPointOfInterestId, setSelectedPointOfInterestId] = useAtom(
    localState.selectedPointOfInterestId
  );
  const setSelectedReviewPointsOfInterest = useSetAtom(
    analysisState.selectedReviewPointsOfInterest
  );
  const setIsAllReviewPointsOfInterestSelected = useSetAtom(
    analysisState.isAllReviewPointsOfInterestSelected
  );
  const setSelectedReviewPoiTypeId = useSetAtom(analysisState.selectedReviewPoiTypeId);
  const setSelectedReviewPoiTypes = useSetAtom(analysisState.selectedReviewPoiTypes);
  const canViewReviewScreen = useFeatureFlag(['api-inspector', 'mi-engineer']);

  const closeHandler = useCallback(() => {
    setSelectedPointOfInterestId(undefined);
    setSelectedReviewPointsOfInterest([]);
    setIsAllReviewPointsOfInterestSelected(false);
    setSelectedReviewPoiTypeId(undefined);
    setSelectedReviewPoiTypes([]);
    return undefined;
  }, [
    setSelectedPointOfInterestId,
    setSelectedReviewPointsOfInterest,
    setIsAllReviewPointsOfInterestSelected,
    setSelectedReviewPoiTypeId,
    setSelectedReviewPoiTypes,
  ]);

  useEffect(() => {
    setSelectedReviewPointsOfInterest([]);
    setIsAllReviewPointsOfInterestSelected(false);
    setSelectedReviewPoiTypeId(undefined);
    setSelectedReviewPoiTypes([]);
  }, [
    setSelectedReviewPointsOfInterest,
    setIsAllReviewPointsOfInterestSelected,
    setSelectedReviewPoiTypeId,
    setSelectedReviewPoiTypes,
  ]);

  const selectedPoiDisplayComponent = useMemo(
    () => (
      <SelectedPoiContent
        selectedPoiId={selectedPointOfInterestId as string}
        closeHandler={closeHandler}
      />
    ),
    [closeHandler, selectedPointOfInterestId]
  );

  if (selectedPointOfInterestId && selectedPointOfInterestId.length > 0) {
    return selectedPoiDisplayComponent;
  }

  if (canViewReviewScreen) {
    return (
      <ReviewPointsOfInterestByType
        pois={pois}
        changeTab={changeTab}
        allPOILoading={allPOILoading}
      />
    );
  }

  return <PointsOfInterestByType pois={pois} changeTab={changeTab} allPOILoading={allPOILoading} />;
};
